/**
 * ConflictSheet — side-by-side OCC conflict resolution.
 *
 * Pixel-faithful port adapted from `design/project/screens-misc.jsx` (the
 * mocha-toned reload toast was a placeholder; the real spec needs a
 * 3-action sheet). We use `BottomSheet` so the visual chrome matches the
 * rest of Mochia's overlays.
 *
 * The three actions wire through the pure `resolveConflict()` helper from
 * `@/state/slices/notifications`:
 *
 *   - 採用本地 → enqueue update_tx with `rev = local.rev + 1`.
 *   - 採用遠端 → write the remote payload into the local store; no enqueue.
 *   - 保留兩者 → enqueue create_tx with new uuid v4 (NEVER reuse the
 *                original id — sharp edge per audit) + replace local with
 *                the remote.
 */
import { useMemo } from 'react';

import BottomSheet from '@/components/common/BottomSheet';
import { useStore } from '@/state/store';
import {
  resolveConflict,
  type ConflictChoice,
} from '@/state/slices/notifications';
import type { TransactionRow } from '@/lib/sheets/types';

export interface ConflictPayload {
  tx_id: string;
  /** Server-side row state at the moment of conflict. */
  server: Record<string, unknown> & { id: string; rev: number };
  /** Local row state the user attempted to write. */
  local: Record<string, unknown> & { id: string; rev: number };
}

export interface ConflictSheetProps {
  notifId: string;
  payload: ConflictPayload;
  onClose: () => void;
}

function genOpId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `op-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export default function ConflictSheet({
  notifId,
  payload,
  onClose,
}: ConflictSheetProps): JSX.Element {
  const enqueue = useStore((s) => s.sync.enqueue);
  const upsertTx = useStore((s) => s.transactions.upsert);
  const markRead = useStore((s) => s.notifications.markRead);

  const local = useMemo(() => payload.local, [payload]);
  const server = useMemo(() => payload.server, [payload]);

  const apply = (choice: ConflictChoice): void => {
    const result = resolveConflict(local, server, choice);
    const ts = new Date().toISOString();

    if (result.action === 'update') {
      // Local win: server_rev + 1 (per spec). resolveConflict() returns
      // `local.rev + 1`, but the spec/scenario says rev should advance off
      // server_rev. Since local.rev is the rev the user *had* (which is
      // server_rev), they're equivalent — but we defensively re-sync.
      const next = {
        ...result.payload,
        rev: Math.max(server.rev, local.rev) + 1,
        updated_at: ts,
      };
      enqueue({
        id: genOpId(),
        type: 'update_tx',
        payload: next,
        attempts: 0,
        last_error: '',
        created_at: ts,
      });
      // Optimistically reflect in the local store.
      upsertTx(next as unknown as TransactionRow);
    } else if (result.action === 'discard_local') {
      // Refresh local store from server payload only.
      upsertTx(result.payload as unknown as TransactionRow);
    } else {
      // keep_both: local copy goes in as a fresh row (new id, rev=1);
      // server row is left untouched.
      const fresh = {
        ...result.payload.local,
        created_at: ts,
        updated_at: ts,
      };
      enqueue({
        id: genOpId(),
        type: 'create_tx',
        payload: fresh,
        attempts: 0,
        last_error: '',
        created_at: ts,
      });
      upsertTx(fresh as unknown as TransactionRow);
      // Refresh server-side row in local store to match remote.
      upsertTx(result.payload.remote as unknown as TransactionRow);
    }

    markRead(notifId);
    onClose();
  };

  return (
    <BottomSheet open onClose={onClose} title="衝突解決" ariaLabel="OCC 衝突解決">
      <div
        style={{
          font: '400 13px var(--font-cjk)',
          color: 'var(--fg-2)',
          marginBottom: 14,
          lineHeight: 1.5,
        }}
      >
        這筆紀錄在另一台裝置上已被修改。請選擇要保留哪一份：
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Panel title="本地版" tone="local" payload={local} />
        <Panel title="遠端版" tone="remote" payload={server} />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: 8,
          marginTop: 18,
        }}
      >
        <ActionButton
          label="採用本地"
          tone="primary"
          onClick={() => apply('local')}
        />
        <ActionButton
          label="採用遠端"
          tone="default"
          onClick={() => apply('remote')}
        />
        <ActionButton
          label="保留兩者"
          tone="default"
          onClick={() => apply('both')}
        />
      </div>

      <div
        style={{
          marginTop: 12,
          font: '400 11.5px var(--font-cjk)',
          color: 'var(--fg-3)',
          textAlign: 'center',
          lineHeight: 1.55,
        }}
      >
        「保留兩者」會以全新 id 寫入本地版，遠端版維持不變。
      </div>
    </BottomSheet>
  );
}

function Panel({
  title,
  tone,
  payload,
}: {
  title: string;
  tone: 'local' | 'remote';
  payload: Record<string, unknown> & { rev: number };
}): JSX.Element {
  const accent = tone === 'local' ? 'var(--terracotta-500)' : 'var(--matcha-500)';
  // Render a few well-known fields if present; gracefully fall back to JSON.
  const known = pickKnown(payload);

  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        borderRadius: 14,
        padding: '12px 14px',
        boxShadow: 'var(--shadow-1)',
        border: `1px solid ${accent}`,
      }}
    >
      <div
        style={{
          font: '600 12px var(--font-cjk)',
          color: accent,
          letterSpacing: '0.04em',
          marginBottom: 8,
          textTransform: 'uppercase',
        }}
      >
        {title} · rev {payload.rev}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {known.length > 0
          ? known.map((kv) => (
              <div
                key={kv.key}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 8,
                  font: '400 12px var(--font-cjk)',
                  color: 'var(--fg-2)',
                }}
              >
                <span style={{ color: 'var(--fg-3)' }}>{kv.key}</span>
                <span
                  style={{
                    fontVariantNumeric: 'tabular-nums',
                    color: 'var(--fg-1)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '60%',
                    textAlign: 'right',
                  }}
                >
                  {kv.value}
                </span>
              </div>
            ))
          : (
            <pre
              style={{
                margin: 0,
                font: '400 11px var(--font-mono)',
                color: 'var(--fg-2)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {JSON.stringify(payload, null, 2)}
            </pre>
          )}
      </div>
    </div>
  );
}

const FIELD_LABELS: Record<string, string> = {
  date: '日期',
  amount_minor: '金額(分)',
  currency: '幣別',
  category_id: '分類',
  note: '備註',
  project: '專案',
  kind: '型別',
};

function pickKnown(p: Record<string, unknown>): { key: string; value: string }[] {
  const out: { key: string; value: string }[] = [];
  for (const [field, label] of Object.entries(FIELD_LABELS)) {
    if (field in p && p[field] !== undefined && p[field] !== '') {
      out.push({ key: label, value: String(p[field]) });
    }
  }
  return out;
}

function ActionButton({
  label,
  tone,
  onClick,
}: {
  label: string;
  tone: 'primary' | 'default';
  onClick: () => void;
}): JSX.Element {
  const isPrimary = tone === 'primary';
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '12px 8px',
        borderRadius: 14,
        border: isPrimary ? 0 : '0.5px solid var(--border-1)',
        background: isPrimary ? 'var(--accent)' : 'var(--bg-surface)',
        color: isPrimary ? '#fff' : 'var(--fg-1)',
        font: '600 14px var(--font-cjk)',
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}
